(() => {
  const CUSTOM_ELEMENT_NAME = "ag-main-article";
  const CONSULTATION_BADGE_ELEMENT_NAME = "ag-consultation-badge";
  const CONSULTATION_MODAL_ELEMENT_NAME = "ag-consultation-modal";
  const CONTENT_SELECTOR = "[data-ag-main-article-content]";
  const ARTICLE_TITLE_SELECTOR = ".ag-main-article__title";
  const TOC_SELECTOR = "[data-ag-main-article-toc]";
  const TOC_HEADING_SELECTOR = ".ag-main-article__toc-heading";
  const TOC_LIST_SELECTOR = "[data-ag-main-article-toc-list]";
  const TOC_SCROLL_SELECTOR = "[data-ag-main-article-toc-scroll]";
  const TOC_LINK_SELECTOR = ".ag-main-article__toc-link";
  const SECTION_HEADER_SELECTOR = ".section-header";
  const STICKY_HEADER_SELECTOR = "sticky-header";
  const FAQ_TEMPLATE_SELECTOR = "[data-ag-main-article-faq-template]";
  const CTA_TEMPLATE_SELECTOR = "[data-ag-main-article-cta-template]";
  const CONSULTATION_SELECTOR = "[data-ag-main-article-consultation]";
  const CONSULTATION_BADGE_BUTTON_SELECTOR =
    "[data-ag-consultation-badge-button]";
  const CONSULTATION_BADGE_CLOSE_SELECTOR =
    "[data-ag-consultation-badge-close]";
  const CONSULTATION_MODAL_SELECTOR = "[data-ag-consultation-modal]";
  const CONSULTATION_MODAL_CLOSE_SELECTOR =
    "[data-ag-consultation-modal-close]";
  const CONSULTATION_MODAL_BOOKER_SELECTOR =
    "[data-ag-consultation-modal-booker]";
  const CONSULTATION_BADGE_MOBILE_QUERY = "(max-width: 1199px)";
  const HEADING_SELECTOR = "h1, h2, h3, h4, h5, h6";
  const FAQ_SHORTCODE = "[ag_faq]";
  const CTA_SHORTCODE_PATTERN = /^\[ag_cta:([a-zA-Z0-9_-]+)\]$/;
  const AG_SHORTCODE_PATTERN = /^\[ag_[^\]]*\]$/;
  const ACTIVE_CLASS = "is-active";
  const GENERATED_HEADING_DATA_KEY = "agMainArticleHeadingId";
  const CONSULTATION_EVENT_NAME = "ag:consultation:open";
  const CONSULTATION_CLOSE_EVENT_NAME = "ag:consultation:close";
  const CAL_EMBED_SCRIPT_URL = "https://app.cal.com/embed/embed.js";
  const CAL_ORIGIN = "https://cal.com";
  const CAL_MODAL_LAYOUT = "month_view";
  const CAL_PRELOAD_IDLE_TIMEOUT = 2000;
  const CAL_PRELOAD_INTENT_EVENTS = [
    "pointerover",
    "focusin",
    "pointerdown",
    "touchstart",
  ];
  const FIXED_BADGE_CTA_KEY = "fixed-badge";
  const INTERSECTION_THRESHOLDS = [0, 1];
  const ACTIVE_HEADING_OFFSET = 40;
  const MAX_ACTIVE_VIEWPORT_RATIO = 0.4;
  const PAGE_TOP_EPSILON = 2;
  const ARTICLE_END_EPSILON = 2;
  const SCROLL_EDGE_EPSILON = 0.5;
  const DEFAULT_LINE_HEIGHT = 16;
  const TOC_PRECEDING_CONTEXT_COUNT = 2;
  const TOC_SCROLL_MIN_DURATION = 180;
  const TOC_SCROLL_MAX_DURATION = 340;
  const PAGE_SCROLL_MIN_DURATION = 260;
  const PAGE_SCROLL_MAX_DURATION = 620;
  const PAGE_SCROLL_HEADER_GAP = 20;
  const HEADER_NAVIGATION_LOCK_ATTRIBUTE = "data-ag-toc-navigation-lock";
  const HEADER_HIDDEN_CLASS = "shopify-section-header-hidden";
  const HEADER_STICKY_CLASS = "shopify-section-header-sticky";
  const HEADER_SCROLLED_CLASS = "scrolled-past-header";
  const HEADER_ANIMATE_CLASS = "animate";
  const HEADER_TRANSITION_EPSILON = 0.75;
  const HEADER_TRANSITION_MAX_DURATION = 300;
  const WHEEL_SEQUENCE_IDLE_DURATION = 180;
  const MANUAL_SCROLL_KEYS = new Set([
    "ArrowUp",
    "ArrowDown",
    "PageUp",
    "PageDown",
    "Home",
    "End",
    " ",
  ]);

  let calEmbedPromise = null;

  function dispatchConsultationEvent(trigger, detail) {
    return trigger.dispatchEvent(
      new CustomEvent(CONSULTATION_EVENT_NAME, {
        bubbles: true,
        cancelable: true,
        detail,
      }),
    );
  }

  function getCalLink(value) {
    const rawValue = value?.trim();
    if (!rawValue) return "";

    try {
      const parsedUrl = new URL(rawValue, CAL_ORIGIN);
      const isCalHost =
        parsedUrl.hostname === "cal.com" ||
        parsedUrl.hostname.endsWith(".cal.com");
      const path = parsedUrl.pathname.replace(/^\/+|\/+$/g, "");
      return !isCalHost || !path ? "" : `${path}${parsedUrl.search}`;
    } catch {
      return "";
    }
  }

  function createCalQueue() {
    if (window.Cal) return;
    const cal = (...args) => cal.q.push(args);
    cal.q = [];
    cal.ns = {};
    window.Cal = cal;
  }

  function loadCalEmbed() {
    if (window.Cal?.version) return Promise.resolve(window.Cal);
    if (calEmbedPromise) return calEmbedPromise;

    createCalQueue();
    calEmbedPromise = new Promise((resolve, reject) => {
      const existingScript = document.querySelector(
        `script[src="${CAL_EMBED_SCRIPT_URL}"]`,
      );
      const script = existingScript || document.createElement("script");

      const handleLoad = () => resolve(window.Cal);
      const handleError = () => {
        calEmbedPromise = null;
        reject(new Error("Cal.com embed failed to load."));
      };

      script.addEventListener("load", handleLoad, { once: true });
      script.addEventListener("error", handleError, { once: true });

      if (!existingScript) {
        script.src = CAL_EMBED_SCRIPT_URL;
        script.async = true;
        document.head.appendChild(script);
      }
    });

    return calEmbedPromise;
  }

  function configureCalUi(cal, accentColor) {
    const brandColor = accentColor || "#1a5bff";

    cal("ui", {
      theme: "light",
      hideEventTypeDetails: false,
      cssVarsPerTheme: {
        light: {
          "cal-brand": brandColor,
          "cal-brand-emphasis": brandColor,
          "cal-brand-text": "#ffffff",
          "cal-bg": "#ffffff",
          "cal-border": "#e5e7eb",
          "cal-border-subtle": "#e5e7eb",
          "cal-border-muted": "#e5e7eb",
          "cal-border-booker": "#e5e7eb",
          "cal-border-booker-width": "1px",
          "radius-3xl": "1.5rem",
        },
      },
    });
  }

  function openCalModal(calUrl, accentColor) {
    document
      .querySelector(CONSULTATION_MODAL_ELEMENT_NAME)
      ?.open(calUrl, accentColor);
  }

  class AgConsultationModal extends HTMLElement {
    constructor() {
      super();
      this.dialog = null;
      this.closeButton = null;
      this.booker = null;
      this.bookerObserver = null;
      this.openRequest = 0;
      this.preloadHandle = null;
      this.preloadArmed = false;
      this.waitingForWindowLoad = false;
      this.inlineCalLink = "";
      this.inlineMountPromise = null;
      this.inlineReady = false;
      this.calEventsBound = false;

      this.handleBackdropClick = this.handleBackdropClick.bind(this);
      this.handleBookerMutation = this.handleBookerMutation.bind(this);
      this.handleCalLinkFailed = this.handleCalLinkFailed.bind(this);
      this.handleCalLinkReady = this.handleCalLinkReady.bind(this);
      this.handleClose = this.handleClose.bind(this);
      this.handleCloseClick = this.handleCloseClick.bind(this);
      this.handlePreloadIntent = this.handlePreloadIntent.bind(this);
      this.handleWindowLoad = this.handleWindowLoad.bind(this);
    }

    connectedCallback() {
      this.dialog = this.querySelector(CONSULTATION_MODAL_SELECTOR);
      this.closeButton = this.querySelector(CONSULTATION_MODAL_CLOSE_SELECTOR);
      this.booker = this.querySelector(CONSULTATION_MODAL_BOOKER_SELECTOR);

      this.dialog?.addEventListener("click", this.handleBackdropClick);
      this.dialog?.addEventListener("close", this.handleClose);
      this.closeButton?.addEventListener("click", this.handleCloseClick);

      if (this.booker) {
        this.bookerObserver = new MutationObserver(this.handleBookerMutation);
        this.bookerObserver.observe(this.booker, {
          childList: true,
          subtree: true,
        });
      }

      /* AG CAL.COM POPUP PRELOAD START */
      this.schedulePreload();
      /* AG CAL.COM POPUP PRELOAD END */
    }

    disconnectedCallback() {
      this.dialog?.removeEventListener("click", this.handleBackdropClick);
      this.dialog?.removeEventListener("close", this.handleClose);
      this.closeButton?.removeEventListener("click", this.handleCloseClick);
      this.bookerObserver?.disconnect();
      this.bookerObserver = null;
      this.cancelScheduledPreload();
      this.unbindCalEvents();
      document.documentElement.classList.remove(
        "ag-consultation-modal-is-open",
      );
    }

    async open(calUrl, accentColor) {
      const calLink = getCalLink(calUrl);
      if (!calLink || !this.dialog || !this.booker) return;

      this.cancelScheduledPreload();
      const request = ++this.openRequest;
      const hasMountedInline =
        this.inlineCalLink === calLink &&
        Boolean(this.booker.querySelector("iframe"));

      if (!hasMountedInline || !this.inlineReady) {
        this.booker.setAttribute("aria-busy", "true");
        this.classList.add("is-loading");
      } else {
        this.booker.removeAttribute("aria-busy");
        this.classList.remove("is-loading");
      }

      if (!this.dialog.open) {
        this.dialog.showModal();
      }
      document.documentElement.classList.add("ag-consultation-modal-is-open");

      try {
        const cal = await this.mountInline(calLink, accentColor);
        if (request !== this.openRequest || !this.dialog.open) return;

        // The trigger can use a different accent than the background preload.
        configureCalUi(cal, accentColor);
        if (this.inlineReady) {
          this.finishLoading(request);
        }
      } catch (error) {
        if (request === this.openRequest) {
          this.dialog.close();
        }
        console.error(error);
      }
    }

    mountInline(calLink, accentColor) {
      const existingIframe = this.booker?.querySelector("iframe");
      if (
        this.inlineCalLink === calLink &&
        existingIframe &&
        window.Cal?.version
      ) {
        configureCalUi(window.Cal, accentColor);
        return Promise.resolve(window.Cal);
      }

      if (this.inlineMountPromise) {
        return this.inlineMountPromise.then((cal) => {
          configureCalUi(cal, accentColor);
          return cal;
        });
      }

      this.inlineReady = false;
      this.inlineMountPromise = loadCalEmbed()
        .then((cal) => {
          if (!this.booker) {
            throw new Error("Cal.com inline container is unavailable.");
          }

          cal("init", { origin: CAL_ORIGIN });
          configureCalUi(cal, accentColor);
          this.bindCalEvents(cal);

          this.booker.replaceChildren();
          cal("inline", {
            elementOrSelector: this.booker,
            calLink,
            config: { layout: CAL_MODAL_LAYOUT },
          });
          this.inlineCalLink = calLink;
          return cal;
        })
        .catch((error) => {
          this.inlineMountPromise = null;
          this.inlineCalLink = "";
          throw error;
        });

      return this.inlineMountPromise;
    }

    bindCalEvents(cal) {
      if (this.calEventsBound) return;

      cal("on", {
        action: "linkReady",
        callback: this.handleCalLinkReady,
      });
      cal("on", {
        action: "linkFailed",
        callback: this.handleCalLinkFailed,
      });
      this.calEventsBound = true;
    }

    unbindCalEvents() {
      if (!this.calEventsBound || !window.Cal?.version) return;

      window.Cal("off", {
        action: "linkReady",
        callback: this.handleCalLinkReady,
      });
      window.Cal("off", {
        action: "linkFailed",
        callback: this.handleCalLinkFailed,
      });
      this.calEventsBound = false;
    }

    handleCalLinkReady() {
      this.inlineReady = true;
      if (this.dialog?.open) {
        this.finishLoading(this.openRequest);
      }
    }

    handleCalLinkFailed() {
      this.inlineReady = false;
      this.inlineMountPromise = null;
      this.inlineCalLink = "";
      if (this.dialog?.open) {
        this.finishLoading(this.openRequest);
      }
    }

    finishLoading(request) {
      if (request !== this.openRequest || !this.dialog?.open) return;
      this.classList.remove("is-loading");
      this.booker?.removeAttribute("aria-busy");
    }

    schedulePreload() {
      const calUrl = this.dataset.calUrl;
      if (
        !getCalLink(calUrl) ||
        this.waitingForWindowLoad ||
        this.preloadArmed ||
        this.inlineMountPromise
      ) {
        return;
      }

      if (document.readyState === "complete") {
        this.armPreloadIntentListeners();
        return;
      }

      this.waitingForWindowLoad = true;
      window.addEventListener("load", this.handleWindowLoad, { once: true });
    }

    handleWindowLoad() {
      this.waitingForWindowLoad = false;
      if (this.isConnected) {
        this.armPreloadIntentListeners();
      }
    }

    armPreloadIntentListeners() {
      if (this.preloadArmed || this.inlineMountPromise) return;

      this.preloadArmed = true;
      CAL_PRELOAD_INTENT_EVENTS.forEach((eventName) => {
        window.addEventListener(eventName, this.handlePreloadIntent, {
          passive: true,
        });
      });
    }

    removePreloadIntentListeners() {
      if (!this.preloadArmed) return;

      CAL_PRELOAD_INTENT_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, this.handlePreloadIntent);
      });
      this.preloadArmed = false;
    }

    handlePreloadIntent(event) {
      const trigger = event.target instanceof Element && event.target.closest(
        `${CONSULTATION_SELECTOR}, ${CONSULTATION_BADGE_BUTTON_SELECTOR}`,
      );
      if (!trigger) return;

      this.removePreloadIntentListeners();

      const runPreload = () => {
        this.preloadHandle = null;
        this.startInlinePreload();
      };

      if ("requestIdleCallback" in window) {
        this.preloadHandle = {
          type: "idle",
          id: window.requestIdleCallback(runPreload, {
            timeout: CAL_PRELOAD_IDLE_TIMEOUT,
          }),
        };
        return;
      }

      this.preloadHandle = {
        type: "timeout",
        id: window.setTimeout(runPreload, 0),
      };
    }

    startInlinePreload() {
      const calLink = getCalLink(this.dataset.calUrl);
      if (!calLink || !this.booker || this.dialog?.open) return;

      const accentColor =
        document.querySelector(
          CONSULTATION_BADGE_ELEMENT_NAME + "[data-cal-accent]",
        )?.dataset.calAccent ||
        document.querySelector(CUSTOM_ELEMENT_NAME + "[data-cal-accent]")
          ?.dataset.calAccent ||
        "#1a5bff";

      this.mountInline(calLink, accentColor).catch(() => {
        // A regular open retries if the background preload fails.
      });
    }

    cancelScheduledPreload() {
      if (this.waitingForWindowLoad) {
        window.removeEventListener("load", this.handleWindowLoad);
        this.waitingForWindowLoad = false;
      }

      this.removePreloadIntentListeners();
      if (this.preloadHandle === null) return;

      if (
        this.preloadHandle.type === "idle" &&
        "cancelIdleCallback" in window
      ) {
        window.cancelIdleCallback(this.preloadHandle.id);
      } else {
        window.clearTimeout(this.preloadHandle.id);
      }
      this.preloadHandle = null;
    }
    /* AG CAL.COM POPUP PRELOAD END */

    handleBackdropClick(event) {
      if (event.target === this.dialog) {
        this.dialog.close();
      }
    }

    handleBookerMutation() {
      this.booker?.querySelectorAll("iframe").forEach((iframe) => {
        iframe.setAttribute("scrolling", "no");
      });
    }

    handleCloseClick() {
      this.dialog?.close();
    }

    handleClose() {
      this.openRequest += 1;
      this.classList.remove("is-loading");
      this.booker?.removeAttribute("aria-busy");
      document.documentElement.classList.remove(
        "ag-consultation-modal-is-open",
      );
      this.dispatchEvent(
        new CustomEvent(CONSULTATION_CLOSE_EVENT_NAME, { bubbles: true }),
      );
    }
  }

  class AgMainArticle extends HTMLElement {
    constructor() {
      super();
      this.contentRoot = null;
      this.toc = null;
      this.tocList = null;
      this.tocScroll = null;
      this.headingObserver = null;
      this.tocLayoutObserver = null;
      this.headingLinks = new Map();
      this.headings = [];
      this.activeHeading = null;
      this.forcedActiveHeading = null;
      this.viewportFrame = null;
      this.tocScrollFrame = null;
      this.tocLayoutFrame = null;
      this.pageScrollFrame = null;
      this.pageNavigation = null;
      this.suppressNextAutoReveal = false;
      this.pendingViewportScroll = false;
      this.lastTocWheelEvent = null;
      this.lastTocWheelPageMovement = 0;
      this.tocWheelResidual = 0;
      this.tocWheelDirection = 0;
      this.lastTocWheelTimestamp = 0;
      this.pendingTocRevealHeading = null;
      this.isTocHovered = false;
      this.hasTocWheelOwnership = false;
      this.isInitialized = false;
      this.isDesignMode = false;

      this.handleClick = this.handleClick.bind(this);
      this.handleHeadingIntersection =
        this.handleHeadingIntersection.bind(this);
      this.handleViewportChange = this.handleViewportChange.bind(this);
      this.handleManualPageScrollIntent =
        this.handleManualPageScrollIntent.bind(this);
      this.handleTocWheel = this.handleTocWheel.bind(this);
      this.handleTocManualIntent = this.handleTocManualIntent.bind(this);
      this.handleTocLayoutResize = this.handleTocLayoutResize.bind(this);
      this.handleTocPointerEnter = this.handleTocPointerEnter.bind(this);
      this.handleTocPointerLeave = this.handleTocPointerLeave.bind(this);
      this.handleShopifySectionLoad = this.handleShopifySectionLoad.bind(this);
    }

    connectedCallback() {
      if (this.isInitialized) return;
      this.isInitialized = true;
      this.addEventListener("click", this.handleClick);
      document.addEventListener(
        "shopify:section:load",
        this.handleShopifySectionLoad,
      );
      this.refreshArticle();
    }

    disconnectedCallback() {
      if (!this.isInitialized) return;

      this.removeEventListener("click", this.handleClick);
      document.removeEventListener(
        "shopify:section:load",
        this.handleShopifySectionLoad,
      );
      this.stopTocInteractions();
      this.disconnectHeadingObserver();
      this.stopHeadingTracking();
      this.cancelTocAutoScroll();
      this.cancelPageNavigation();
      this.headingLinks.clear();
      this.headings = [];
      this.activeHeading = null;
      this.forcedActiveHeading = null;
      this.suppressNextAutoReveal = false;
      this.pendingViewportScroll = false;
      this.lastTocWheelEvent = null;
      this.lastTocWheelPageMovement = 0;
      this.pendingTocRevealHeading = null;
      this.hasTocWheelOwnership = false;
      this.resetTocWheelResidual();
      this.contentRoot = null;
      this.toc = null;
      this.tocList = null;
      this.tocScroll = null;
      this.isInitialized = false;
    }

    refreshArticle() {
      this.stopTocInteractions();
      this.disconnectHeadingObserver();
      this.stopHeadingTracking();
      this.cancelTocAutoScroll();
      this.cancelPageNavigation();
      this.headingLinks.clear();
      this.headings = [];
      this.activeHeading = null;
      this.forcedActiveHeading = null;
      this.suppressNextAutoReveal = false;
      this.pendingViewportScroll = false;
      this.lastTocWheelEvent = null;
      this.lastTocWheelPageMovement = 0;
      this.pendingTocRevealHeading = null;
      this.hasTocWheelOwnership = false;
      this.resetTocWheelResidual();

      this.contentRoot = this.querySelector(CONTENT_SELECTOR);
      this.toc = this.querySelector(TOC_SELECTOR);
      this.tocList = this.querySelector(TOC_LIST_SELECTOR);
      this.tocScroll = null;
      this.isDesignMode = this.dataset.designMode === "true";

      if (!this.contentRoot) return;

      this.ensureTocStructure();
      this.processShortcodes();
      this.buildTableOfContents();
      this.startTocInteractions();
    }

    handleShopifySectionLoad(event) {
      const sectionRoot = event.target;
      if (!(sectionRoot instanceof Node)) return;
      if (sectionRoot !== this && !sectionRoot.contains(this)) return;
      this.refreshArticle();
    }

    /* AG TOC PATCH START: create a dedicated scroll viewport when older Liquid
     * markup still renders the heading and list as direct children of <nav>. */
    ensureTocStructure() {
      if (!this.toc || !this.tocList) return;

      const tocHeading = this.toc.querySelector(TOC_HEADING_SELECTOR);
      if (tocHeading) {
        const baseId = tocHeading.id || "ag-main-article-toc-heading";
        let uniqueId = baseId;
        let suffix = 2;
        while (
          document.getElementById(uniqueId) &&
          document.getElementById(uniqueId) !== tocHeading
        ) {
          uniqueId = `${baseId}-${suffix}`;
          suffix += 1;
        }
        tocHeading.id = uniqueId;
        this.toc.setAttribute("aria-labelledby", uniqueId);
      }

      this.tocScroll = this.toc.querySelector(TOC_SCROLL_SELECTOR);
      if (!this.tocScroll) {
        this.tocScroll = document.createElement("div");
        this.tocScroll.className = "ag-main-article__toc-scroll";
        this.tocScroll.dataset.agMainArticleTocScroll = "";
        this.tocScroll.tabIndex = 0;
        this.tocScroll.setAttribute("aria-label", this.dataset.tocLinksLabel || tocHeading?.textContent?.trim() || "");
        this.tocList.before(this.tocScroll);
        this.tocScroll.append(this.tocList);
      }
    }
    /* AG TOC PATCH END */

    startTocInteractions() {
      if (!this.toc || !this.tocScroll) return;

      this.toc.addEventListener("wheel", this.handleTocWheel, {
        capture: true,
        passive: false,
      });
      this.tocScroll.addEventListener(
        "touchstart",
        this.handleTocManualIntent,
        {
          passive: true,
        },
      );
      this.tocScroll.addEventListener(
        "pointerdown",
        this.handleTocManualIntent,
        {
          passive: true,
        },
      );
      this.tocScroll.addEventListener("keydown", this.handleTocManualIntent);
      this.toc.addEventListener("mouseenter", this.handleTocPointerEnter);
      this.toc.addEventListener("mouseleave", this.handleTocPointerLeave);
      if ("ResizeObserver" in window) {
        this.tocLayoutObserver = new ResizeObserver(this.handleTocLayoutResize);
        this.tocLayoutObserver.observe(this.tocScroll);
        this.tocLayoutObserver.observe(this.tocList);
      }
      this.isTocHovered = this.canHoverToc() && this.toc.matches(":hover");
    }

    stopTocInteractions() {
      this.toc?.removeEventListener("wheel", this.handleTocWheel, true);
      this.tocScroll?.removeEventListener(
        "touchstart",
        this.handleTocManualIntent,
      );
      this.tocScroll?.removeEventListener(
        "pointerdown",
        this.handleTocManualIntent,
      );
      this.tocScroll?.removeEventListener(
        "keydown",
        this.handleTocManualIntent,
      );
      this.toc?.removeEventListener("mouseenter", this.handleTocPointerEnter);
      this.toc?.removeEventListener("mouseleave", this.handleTocPointerLeave);
      this.tocLayoutObserver?.disconnect();
      this.tocLayoutObserver = null;
      if (this.tocLayoutFrame !== null) {
        window.cancelAnimationFrame(this.tocLayoutFrame);
        this.tocLayoutFrame = null;
      }
      this.pendingTocRevealHeading = null;
      this.isTocHovered = false;
      this.hasTocWheelOwnership = false;
    }

    handleTocPointerEnter() {
      this.isTocHovered = true;
      this.hasTocWheelOwnership = false;
      this.cancelTocAutoScroll();
      this.pendingTocRevealHeading = null;
      this.resetTocWheelResidual();
    }

    handleTocPointerLeave() {
      this.isTocHovered = false;
      this.hasTocWheelOwnership = false;
      this.resetTocWheelResidual();
      // Deliberately do not “catch up” here. The next active-section change may
      // adjust the list, so leaving the menu never produces a delayed jump.
    }

    handleTocManualIntent(event) {
      if (event.type === "keydown" && !MANUAL_SCROLL_KEYS.has(event.key))
        return;
      this.cancelTocAutoScroll();
      this.pendingTocRevealHeading = null;
      this.cancelPageNavigation();
      this.resetTocWheelResidual();
    }

    handleTocLayoutResize() {
      if (this.tocLayoutFrame !== null) return;
      this.tocLayoutFrame = window.requestAnimationFrame(() => {
        this.tocLayoutFrame = null;
        const heading = this.pendingTocRevealHeading;
        if (
          !heading ||
          heading !== this.activeHeading ||
          this.isTocAutoScrollBlocked()
        ) {
          return;
        }
        const activeEntry = this.headingLinks.get(heading);
        if (activeEntry)
          this.centerTocItem(activeEntry.item, { instant: true });
      });
    }

    canHoverToc() {
      return window.matchMedia("(hover: hover)").matches;
    }

    isTocAutoScrollBlocked({ allowWhileHovered = false } = {}) {
      if (this.hasTocWheelOwnership) return true;
      if (allowWhileHovered) return false;
      return (
        this.isTocHovered ||
        (this.canHoverToc() && Boolean(this.toc?.matches(":hover")))
      );
    }

    /* AG TOC PATCH START: synchronous wheel handoff.
     * There is no timeout, smooth page-scroll queue or delayed RAF target.
     * Only the delta not consumed by the links viewport is applied immediately
     * to the document. Capturing the first event makes this handler the sole
     * owner of the vertical component while Chromium keeps the wheel sequence
     * latched to the inner scroller. */
    handleTocWheel(event) {
      this.cancelTocAutoScroll();
      this.pendingTocRevealHeading = null;
      this.cancelPageNavigation();

      if (!this.tocScroll || event.ctrlKey || event.deltaY === 0) {
        return;
      }

      // The first wheel event must be consumed even when a trackpad reports a
      // larger horizontal component. Letting that event go native makes later
      // events in Chromium's latched transaction non-blocking at the TOC edge.
      if (!event.cancelable) {
        this.resetTocWheelResidual();
        return;
      }

      const delta = this.getAccumulatedTocWheelDelta(event);
      const maxScrollTop = Math.max(
        0,
        this.tocScroll.scrollHeight - this.tocScroll.clientHeight,
      );
      const currentScrollTop = this.tocScroll.scrollTop;
      const nextScrollTop = Math.min(
        maxScrollTop,
        Math.max(0, currentScrollTop + delta),
      );

      event.preventDefault();
      this.tocScroll.scrollTop = nextScrollTop;
      const consumedDelta = this.tocScroll.scrollTop - currentScrollTop;
      if (Math.abs(consumedDelta) > SCROLL_EDGE_EPSILON) {
        this.hasTocWheelOwnership = true;
      }
      const remainingDelta = delta - consumedDelta;
      this.lastTocWheelEvent = event;
      this.lastTocWheelPageMovement = this.scrollPageBy(remainingDelta);
    }

    getAccumulatedTocWheelDelta(event) {
      const rawDelta = this.getWheelDeltaInPixels(event);
      const direction = Math.sign(rawDelta);
      const timestamp = performance.now();

      if (
        direction !== this.tocWheelDirection ||
        timestamp - this.lastTocWheelTimestamp > WHEEL_SEQUENCE_IDLE_DURATION
      ) {
        this.tocWheelResidual = 0;
      }

      const accumulatedDelta = rawDelta + this.tocWheelResidual;
      const wholePixelDelta =
        accumulatedDelta > 0
          ? Math.floor(accumulatedDelta)
          : Math.ceil(accumulatedDelta);
      this.tocWheelResidual = accumulatedDelta - wholePixelDelta;
      this.tocWheelDirection = direction;
      this.lastTocWheelTimestamp = timestamp;
      return wholePixelDelta;
    }

    resetTocWheelResidual() {
      this.tocWheelResidual = 0;
      this.tocWheelDirection = 0;
      this.lastTocWheelTimestamp = 0;
    }

    getWheelDeltaInPixels(event) {
      if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) {
        const lineHeight = Number.parseFloat(
          window.getComputedStyle(this.tocScroll).lineHeight,
        );
        return event.deltaY * (lineHeight || DEFAULT_LINE_HEIGHT);
      }

      if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) {
        return event.deltaY * window.innerHeight;
      }

      return event.deltaY;
    }

    scrollPageBy(delta) {
      const scrollingElement = document.scrollingElement;
      if (!scrollingElement || delta === 0) return 0;

      const start = scrollingElement.scrollTop;
      scrollingElement.scrollTop = start + this.getAvailablePageMovement(delta);
      return scrollingElement.scrollTop - start;
    }

    getAvailablePageMovement(delta) {
      const scrollingElement = document.scrollingElement;
      if (!scrollingElement || delta === 0) return 0;

      const start = scrollingElement.scrollTop;
      const maxScrollTop = Math.max(
        0,
        scrollingElement.scrollHeight - scrollingElement.clientHeight,
      );
      return Math.min(maxScrollTop, Math.max(0, start + delta)) - start;
    }
    /* AG TOC PATCH END */

    processShortcodes() {
      const faqTemplate = this.querySelector(FAQ_TEMPLATE_SELECTOR);
      const ctaTemplates = new Map(
        Array.from(this.querySelectorAll(CTA_TEMPLATE_SELECTOR)).map(
          (template) => [template.dataset.ctaKey, template],
        ),
      );
      let faqRendered = false;

      this.getShortcodeCandidates().forEach((candidate) => {
        const shortcode = candidate.textContent.trim();

        if (shortcode === FAQ_SHORTCODE) {
          if (faqRendered) {
            candidate.remove();
            return;
          }
          if (!faqTemplate) {
            this.replaceWithDiagnostic(
              candidate,
              this.dataset.invalidShortcodeMessage,
            );
            return;
          }
          candidate.replaceWith(faqTemplate.content.cloneNode(true));
          faqRendered = true;
          return;
        }

        const ctaMatch = shortcode.match(CTA_SHORTCODE_PATTERN);
        if (ctaMatch) {
          const ctaTemplate = ctaTemplates.get(ctaMatch[1]);
          if (!ctaTemplate) {
            this.replaceWithDiagnostic(
              candidate,
              this.dataset.unknownCtaMessage,
            );
            return;
          }
          candidate.replaceWith(ctaTemplate.content.cloneNode(true));
          return;
        }

        if (AG_SHORTCODE_PATTERN.test(shortcode)) {
          this.replaceWithDiagnostic(
            candidate,
            this.dataset.invalidShortcodeMessage,
          );
        }
      });
    }

    getShortcodeCandidates() {
      const paragraphCandidates = Array.from(
        this.contentRoot.querySelectorAll("p"),
      );
      const directBlockCandidates = Array.from(
        this.contentRoot.children,
      ).filter(
        (element) => element.matches("div") && !element.querySelector("p, div"),
      );

      return Array.from(
        new Set([...paragraphCandidates, ...directBlockCandidates]),
      ).filter((element) => {
        const value = element.textContent.trim();
        return (
          value === FAQ_SHORTCODE ||
          CTA_SHORTCODE_PATTERN.test(value) ||
          AG_SHORTCODE_PATTERN.test(value)
        );
      });
    }

    replaceWithDiagnostic(candidate, message) {
      if (!this.isDesignMode || !message) {
        candidate.remove();
        return;
      }

      const diagnostic = document.createElement("span");
      diagnostic.className = "ag-main-article__diagnostic";
      diagnostic.textContent = message;
      candidate.replaceWith(diagnostic);
    }

    buildTableOfContents() {
      if (!this.toc || !this.tocList) return;

      this.disconnectHeadingObserver();
      this.stopHeadingTracking();
      this.headingLinks.clear();
      this.tocList.replaceChildren();

      const articleTitle = this.querySelector(ARTICLE_TITLE_SELECTOR);
      const contentHeadings = Array.from(
        this.contentRoot.querySelectorAll(HEADING_SELECTOR),
      );
      const headings = [articleTitle, ...contentHeadings].filter(
        (heading) => heading && heading.textContent.trim() !== "",
      );

      if (headings.length === 0) {
        this.toc.hidden = true;
        return;
      }

      const usedIds = new Set();
      headings.forEach((heading, index) => {
        const id = this.getHeadingId(heading, index, usedIds);
        const item = document.createElement("li");
        const link = document.createElement("a");
        const number = document.createElement("span");
        const label = document.createElement("span");
        const marker = document.createElement("span");

        item.className = "ag-main-article__toc-item";
        item.dataset.headingLevel = heading.tagName.slice(1);
        link.className = "ag-main-article__toc-link";
        link.href = `#${id}`;
        number.className = "ag-main-article__toc-number";
        number.setAttribute("aria-hidden", "true");
        number.textContent = `${index + 1}.`;
        label.className = "ag-main-article__toc-label";
        label.textContent = heading.textContent.trim();
        marker.className = "ag-main-article__toc-marker";
        marker.setAttribute("aria-hidden", "true");

        link.append(number, label, marker);
        item.append(link);
        this.tocList.append(item);
        this.headingLinks.set(heading, { item, link });
      });

      this.toc.hidden = false;
      this.observeHeadings(headings);
    }

    getHeadingId(heading, index, usedIds) {
      let baseId =
        heading.dataset[GENERATED_HEADING_DATA_KEY] ||
        heading.id ||
        this.slugify(heading.textContent) ||
        `section-${index + 1}`;
      let uniqueId = baseId;
      let suffix = 2;

      while (usedIds.has(uniqueId)) {
        uniqueId = `${baseId}-${suffix}`;
        suffix += 1;
      }

      usedIds.add(uniqueId);
      heading.id = uniqueId;
      heading.dataset[GENERATED_HEADING_DATA_KEY] = uniqueId;
      return uniqueId;
    }

    slugify(value) {
      return value
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\u00c0-\u024f\u0400-\u04ff]+/g, "-")
        .replace(/^-+|-+$/g, "");
    }

    observeHeadings(headings) {
      this.headings = headings;
      window.addEventListener("scroll", this.handleViewportChange, {
        passive: true,
      });
      window.addEventListener("resize", this.handleViewportChange);
      window.addEventListener("wheel", this.handleManualPageScrollIntent, {
        passive: false,
      });
      window.addEventListener("touchstart", this.handleManualPageScrollIntent, {
        passive: true,
      });
      window.addEventListener("pointerdown", this.handleManualPageScrollIntent);
      window.addEventListener("keydown", this.handleManualPageScrollIntent);

      if ("IntersectionObserver" in window) {
        this.headingObserver = new IntersectionObserver(
          this.handleHeadingIntersection,
          {
            rootMargin: "0px",
            threshold: INTERSECTION_THRESHOLDS,
          },
        );
        headings.forEach((heading) => this.headingObserver.observe(heading));
      }

      this.scheduleActiveHeadingUpdate();
    }

    handleHeadingIntersection() {
      if (this.pageNavigation) return;
      this.scheduleActiveHeadingUpdate();
    }

    handleViewportChange(event) {
      if (this.pageNavigation) return;
      if (event?.type === "scroll") this.pendingViewportScroll = true;
      this.scheduleActiveHeadingUpdate();
    }

    scheduleActiveHeadingUpdate() {
      if (this.viewportFrame !== null) return;
      this.viewportFrame = window.requestAnimationFrame(() => {
        this.viewportFrame = null;
        this.updateActiveHeading();
      });
    }

    updateActiveHeading() {
      if (this.pageNavigation) return;
      if (this.headings.length === 0) return;

      const suppressAutoReveal = this.suppressNextAutoReveal;
      this.suppressNextAutoReveal = false;
      const didPageScroll = this.pendingViewportScroll;
      this.pendingViewportScroll = false;

      const pageScrollTop =
        document.scrollingElement?.scrollTop || window.scrollY;
      const isPageTop = pageScrollTop <= PAGE_TOP_EPSILON;
      const isArticleEndReached =
        Boolean(this.contentRoot) &&
        this.contentRoot.getBoundingClientRect().bottom <=
          window.innerHeight + ARTICLE_END_EPSILON;

      if (
        this.forcedActiveHeading &&
        this.headingLinks.has(this.forcedActiveHeading)
      ) {
        this.setActiveHeading(this.forcedActiveHeading, {
          scrollToTop: isPageTop,
          suppressAutoReveal,
        });
        return;
      }

      const headerHeight =
        Number.parseFloat(
          window
            .getComputedStyle(document.documentElement)
            .getPropertyValue("--header-height"),
        ) || 0;
      const activationLine = Math.min(
        headerHeight + ACTIVE_HEADING_OFFSET,
        window.innerHeight * MAX_ACTIVE_VIEWPORT_RATIO,
      );
      let activeHeading = this.headings[0];

      if (!isPageTop) {
        this.headings.forEach((heading) => {
          if (heading.getBoundingClientRect().top <= activationLine) {
            activeHeading = heading;
          }
        });
      }

      if (
        isArticleEndReached ||
        pageScrollTop + window.innerHeight >=
          document.documentElement.scrollHeight - 2
      ) {
        activeHeading = this.headings[this.headings.length - 1];
      }

      this.setActiveHeading(activeHeading, {
        scrollToTop: isPageTop,
        suppressAutoReveal,
        revealCurrent:
          isArticleEndReached ||
          (didPageScroll && this.isTocHeadingOutsideViewport(activeHeading)),
      });
    }

    setActiveHeading(
      activeHeading,
      {
        scrollToTop = false,
        suppressAutoReveal = false,
        revealCurrent = false,
      } = {},
    ) {
      if (!activeHeading) return;

      const activeChanged = this.activeHeading !== activeHeading;
      if (activeChanged) {
        this.activeHeading = activeHeading;
        this.headingLinks.forEach(({ item, link }, heading) => {
          const isActive = heading === activeHeading;
          item.classList.toggle(ACTIVE_CLASS, isActive);
          if (isActive) link.setAttribute("aria-current", "location");
          else link.removeAttribute("aria-current");
        });
      }

      // Normally auto-reveal is caused only by a real active-heading change.
      // Reaching the end of the article content is the one explicit exception:
      // the final item may already have become active while hover lock was in
      // force, and there is no later heading change that could reveal it.
      // Mouseleave itself still never starts a catch-up animation.
      if ((!activeChanged && !revealCurrent) || suppressAutoReveal) return;

      // Hover lock: visual active state still updates, but the list never moves
      // under the visitor's mouse and any running animation was cancelled on enter.
      if (this.isTocAutoScrollBlocked()) return;

      this.pendingTocRevealHeading = activeHeading;

      if (scrollToTop) {
        this.animateTocScrollTo(0);
        return;
      }

      const activeEntry = this.headingLinks.get(activeHeading);
      if (activeEntry) {
        this.centerTocItem(activeEntry.item);
      }
    }

    isTocHeadingOutsideViewport(heading) {
      if (!this.tocScroll) return false;
      const activeEntry = this.headingLinks.get(heading);
      if (!activeEntry) return false;

      const viewportRect = this.tocScroll.getBoundingClientRect();
      const itemRect = activeEntry.item.getBoundingClientRect();
      return (
        itemRect.top < viewportRect.top - SCROLL_EDGE_EPSILON ||
        itemRect.bottom > viewportRect.bottom + SCROLL_EDGE_EPSILON
      );
    }

    centerTocItem(item, { instant = false, allowWhileHovered = false } = {}) {
      if (
        !this.tocScroll ||
        this.isTocAutoScrollBlocked({ allowWhileHovered })
      ) {
        return;
      }

      const viewportRect = this.tocScroll.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      let targetScrollTop =
        this.tocScroll.scrollTop +
        itemRect.top +
        itemRect.height / 2 -
        (viewportRect.top + viewportRect.height / 2);

      // Aim at the visual center, but keep up to two preceding entries fully
      // visible. Near the top this context wins over mathematically exact
      // centering, so selecting item 3 still shows items 1 and 2.
      const items = this.tocList ? Array.from(this.tocList.children) : [];
      const itemIndex = items.indexOf(item);
      if (itemIndex >= 0) {
        const contextItem =
          items[Math.max(0, itemIndex - TOC_PRECEDING_CONTEXT_COUNT)];
        const contextRect = contextItem.getBoundingClientRect();
        const maximumContextScrollTop =
          this.tocScroll.scrollTop + contextRect.top - viewportRect.top;
        targetScrollTop = Math.min(targetScrollTop, maximumContextScrollTop);
      }

      this.animateTocScrollTo(targetScrollTop, {
        instant,
        allowWhileHovered,
      });
    }

    animateTocScrollTo(
      targetScrollTop,
      { instant = false, allowWhileHovered = false } = {},
    ) {
      if (
        !this.tocScroll ||
        this.isTocAutoScrollBlocked({ allowWhileHovered })
      ) {
        return;
      }

      const maxScrollTop = Math.max(
        0,
        this.tocScroll.scrollHeight - this.tocScroll.clientHeight,
      );
      const target = Math.min(maxScrollTop, Math.max(0, targetScrollTop));
      const start = this.tocScroll.scrollTop;
      const distance = target - start;

      this.cancelTocAutoScroll();
      if (Math.abs(distance) <= SCROLL_EDGE_EPSILON) {
        this.tocScroll.scrollTop = target;
        return;
      }

      const reduceMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;
      if (instant || reduceMotion) {
        this.tocScroll.scrollTop = target;
        return;
      }

      const duration = Math.min(
        TOC_SCROLL_MAX_DURATION,
        Math.max(TOC_SCROLL_MIN_DURATION, Math.abs(distance) * 0.7),
      );
      const startedAt = performance.now();

      const step = (timestamp) => {
        if (
          !this.tocScroll ||
          this.isTocAutoScrollBlocked({ allowWhileHovered })
        ) {
          this.cancelTocAutoScroll();
          return;
        }

        const progress = Math.min(1, (timestamp - startedAt) / duration);
        const easedProgress = 1 - Math.pow(1 - progress, 3);
        this.tocScroll.scrollTop = start + distance * easedProgress;

        if (progress < 1) {
          this.tocScrollFrame = window.requestAnimationFrame(step);
        } else {
          this.tocScroll.scrollTop = target;
          this.tocScrollFrame = null;
        }
      };

      this.tocScrollFrame = window.requestAnimationFrame(step);
    }

    cancelTocAutoScroll() {
      if (this.tocScrollFrame !== null) {
        window.cancelAnimationFrame(this.tocScrollFrame);
        this.tocScrollFrame = null;
      }
    }

    disconnectHeadingObserver() {
      if (!this.headingObserver) return;
      this.headingObserver.disconnect();
      this.headingObserver = null;
    }

    stopHeadingTracking() {
      window.removeEventListener("scroll", this.handleViewportChange);
      window.removeEventListener("resize", this.handleViewportChange);
      window.removeEventListener("wheel", this.handleManualPageScrollIntent);
      window.removeEventListener(
        "touchstart",
        this.handleManualPageScrollIntent,
      );
      window.removeEventListener(
        "pointerdown",
        this.handleManualPageScrollIntent,
      );
      window.removeEventListener("keydown", this.handleManualPageScrollIntent);

      if (this.viewportFrame !== null) {
        window.cancelAnimationFrame(this.viewportFrame);
        this.viewportFrame = null;
      }
    }

    handleManualPageScrollIntent(event) {
      if (event.type === "keydown" && !MANUAL_SCROLL_KEYS.has(event.key))
        return;
      if (
        event.type === "keydown" &&
        event.target instanceof Element &&
        event.target.matches(
          'input, textarea, select, [contenteditable="true"]',
        )
      ) {
        return;
      }

      const pageNavigationWasRunning = this.pageScrollFrame !== null;
      const forcedHeadingWasActive = Boolean(this.forcedActiveHeading);
      const isVerticalWheel =
        event.type === "wheel" && !event.ctrlKey && event.deltaY !== 0;

      this.cancelTocAutoScroll();
      this.pendingTocRevealHeading = null;
      this.cancelPageNavigation();

      // An outside wheel that interrupts controlled click navigation must be
      // applied in this same event. Otherwise Chromium can discard the first
      // takeover delta while the cancelled page animation still owns scroll.
      if (
        isVerticalWheel &&
        pageNavigationWasRunning &&
        this.lastTocWheelEvent !== event &&
        !event.defaultPrevented &&
        event.cancelable
      ) {
        event.preventDefault();
        this.scrollPageBy(this.getWheelDeltaInPixels(event));
      }

      if (!forcedHeadingWasActive) return;
      this.forcedActiveHeading = null;
      this.suppressNextAutoReveal = true;
      this.scheduleActiveHeadingUpdate();
    }

    handleClick(event) {
      const tocLink = event.target.closest(TOC_LINK_SELECTOR);
      if (tocLink && this.toc?.contains(tocLink)) {
        const targetId = decodeURIComponent(tocLink.hash.slice(1));
        const targetHeading = this.headings.find(
          (heading) => heading.id === targetId,
        );

        if (targetHeading) {
          event.preventDefault();
          this.resetTocWheelResidual();
          this.pendingTocRevealHeading = null;
          this.forcedActiveHeading = targetHeading;
          this.cancelTocAutoScroll();
          this.setActiveHeading(targetHeading, { suppressAutoReveal: true });
          const activeEntry = this.headingLinks.get(targetHeading);
          if (
            activeEntry &&
            !this.isTocAutoScrollBlocked({ allowWhileHovered: true })
          ) {
            this.pendingTocRevealHeading = targetHeading;
            this.centerTocItem(activeEntry.item, { allowWhileHovered: true });
          }
          this.animatePageScrollToHeading(targetHeading);
          window.history.replaceState(null, "", `#${targetId}`);
        }
      }

      const consultationButton = event.target.closest(CONSULTATION_SELECTOR);
      if (!consultationButton || !this.contains(consultationButton)) return;

      if (
        dispatchConsultationEvent(consultationButton, {
          source: "article",
          articleHandle: this.dataset.articleHandle || "",
          ctaKey: consultationButton.dataset.ctaKey || "",
        })
      ) {
        openCalModal(this.dataset.calUrl, this.dataset.calAccent);
      }
    }

    animatePageScrollToHeading(heading) {
      const scrollingElement = document.scrollingElement;
      if (!scrollingElement) return;

      this.cancelPageNavigation();
      const headerLease = this.acquireHeaderNavigationLease();
      const navigation = {
        heading,
        headerLease,
        scrollingElement,
        target: null,
      };

      this.pageNavigation = navigation;
      this.preparePageNavigation(navigation);
    }

    preparePageNavigation(navigation) {
      if (this.pageNavigation !== navigation) return;

      const lease = navigation.headerLease;
      if (!lease || this.isHeaderNavigationGeometrySettled(lease)) {
        this.lockHeaderNavigationLease(lease);
        this.startPageNavigation(navigation);
        return;
      }

      // A click can land during the header's existing 150 ms CSS transition.
      // Let that one user-caused transition reach its endpoint before locking
      // geometry; snapping from a partial frame would recreate the flash.
      const startedAt = performance.now();
      const waitForHeader = (timestamp) => {
        if (this.pageNavigation !== navigation) return;
        if (
          this.isHeaderNavigationGeometrySettled(lease) ||
          timestamp - startedAt >= HEADER_TRANSITION_MAX_DURATION
        ) {
          this.lockHeaderNavigationLease(lease);
          this.startPageNavigation(navigation);
          return;
        }
        this.pageScrollFrame = window.requestAnimationFrame(waitForHeader);
      };

      this.pageScrollFrame = window.requestAnimationFrame(waitForHeader);
    }

    startPageNavigation(navigation) {
      if (this.pageNavigation !== navigation) return;

      const { heading, headerLease, scrollingElement } = navigation;
      const headerOffset = headerLease?.reservedHeight || 0;
      const maxScrollTop = Math.max(
        0,
        scrollingElement.scrollHeight - scrollingElement.clientHeight,
      );
      const target = Math.min(
        maxScrollTop,
        Math.max(
          0,
          scrollingElement.scrollTop +
            heading.getBoundingClientRect().top -
            headerOffset -
            PAGE_SCROLL_HEADER_GAP,
        ),
      );
      const start = scrollingElement.scrollTop;
      const distance = target - start;
      navigation.target = target;

      if (Math.abs(distance) <= SCROLL_EDGE_EPSILON) {
        scrollingElement.scrollTop = target;
        this.settlePageNavigation(navigation);
        return;
      }

      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        scrollingElement.scrollTop = target;
        this.settlePageNavigation(navigation);
        return;
      }

      const duration = Math.min(
        PAGE_SCROLL_MAX_DURATION,
        Math.max(PAGE_SCROLL_MIN_DURATION, Math.abs(distance) * 0.22),
      );
      const startedAt = performance.now();

      const step = (timestamp) => {
        if (
          this.pageNavigation !== navigation ||
          !document.documentElement.contains(this)
        ) {
          this.cancelPageNavigation();
          return;
        }

        const progress = Math.min(1, (timestamp - startedAt) / duration);
        const easedProgress = 1 - Math.pow(1 - progress, 3);
        scrollingElement.scrollTop = start + distance * easedProgress;

        if (progress < 1) {
          this.pageScrollFrame = window.requestAnimationFrame(step);
          return;
        }

        scrollingElement.scrollTop = target;
        this.settlePageNavigation(navigation);
      };

      this.pageScrollFrame = window.requestAnimationFrame(step);
    }

    acquireHeaderNavigationLease() {
      const sectionHeader = document.querySelector(SECTION_HEADER_SELECTOR);
      if (!sectionHeader) return null;

      const stickyHeader = sectionHeader.querySelector(STICKY_HEADER_SELECTOR);
      const scrollHandler = stickyHeader?.onScrollHandler;
      const listenerDetached = typeof scrollHandler === "function";
      const previousPreventHide = stickyHeader?.preventHide;
      const headerHeight = Math.max(
        0,
        sectionHeader.getBoundingClientRect().height ||
          Number.parseFloat(
            window
              .getComputedStyle(document.documentElement)
              .getPropertyValue("--header-height"),
          ) ||
          0,
      );
      const headerTopInlineValue =
        sectionHeader.style.getPropertyValue(
          "--ag-toc-navigation-header-top",
        ) || "";
      const headerTopInlinePriority =
        sectionHeader.style.getPropertyPriority(
          "--ag-toc-navigation-header-top",
        ) || "";
      const tocTopInlineValue =
        this.toc?.style.getPropertyValue("--ag-main-article-toc-top") || "";
      const tocTopInlinePriority =
        this.toc?.style.getPropertyPriority("--ag-main-article-toc-top") || "";

      if (listenerDetached) {
        window.removeEventListener("scroll", scrollHandler);
      }
      if (stickyHeader) stickyHeader.preventHide = true;

      return {
        headerHeight,
        headerTopInlinePriority,
        headerTopInlineValue,
        isLocked: false,
        isVisible: !sectionHeader.classList.contains(HEADER_HIDDEN_CLASS),
        listenerDetached,
        previousPreventHide,
        reservedHeight: 0,
        scrollHandler,
        sectionHeader,
        stickyHeader,
        toc: this.toc,
        tocTopInlinePriority,
        tocTopInlineValue,
      };
    }

    isHeaderNavigationGeometrySettled(lease) {
      if (!lease?.sectionHeader?.isConnected) return true;

      lease.isVisible =
        !lease.sectionHeader.classList.contains(HEADER_HIDDEN_CLASS);
      const headerTop = lease.sectionHeader.getBoundingClientRect().top;
      const expectedTop = lease.isVisible ? 0 : -lease.headerHeight;
      return Math.abs(headerTop - expectedTop) <= HEADER_TRANSITION_EPSILON;
    }

    lockHeaderNavigationLease(lease) {
      if (!lease || lease.isLocked || !lease.sectionHeader.isConnected) return;

      const headerRect = lease.sectionHeader.getBoundingClientRect();
      const minimumHeaderTop = -lease.headerHeight;
      const headerTop = Math.min(0, Math.max(minimumHeaderTop, headerRect.top));
      const visibleHeaderHeight = Math.min(
        lease.headerHeight,
        Math.max(0, headerTop + lease.headerHeight),
      );
      const computedTocTop = lease.toc
        ? Number.parseFloat(window.getComputedStyle(lease.toc).top)
        : Number.NaN;
      const tocTop = Number.isFinite(computedTocTop)
        ? computedTocTop
        : lease.toc?.getBoundingClientRect().top || 0;

      lease.isVisible = visibleHeaderHeight > lease.headerHeight / 2;
      lease.reservedHeight = visibleHeaderHeight;
      lease.sectionHeader.style.setProperty(
        "--ag-toc-navigation-header-top",
        `${headerTop}px`,
      );
      lease.toc?.style.setProperty("--ag-main-article-toc-top", `${tocTop}px`);
      lease.sectionHeader.setAttribute(
        HEADER_NAVIGATION_LOCK_ATTRIBUTE,
        lease.isVisible ? "visible" : "hidden",
      );
      if (!lease.sectionHeader.classList.contains(HEADER_STICKY_CLASS)) {
        lease.sectionHeader.classList.add(HEADER_STICKY_CLASS);
      }
      lease.isLocked = true;
    }

    settlePageNavigation(navigation) {
      if (this.pageNavigation !== navigation) return;

      // Keep the header lease for two frames after the last scrollTop write.
      // This drains scroll events/queued theme RAF work without a timeout or a
      // corrective page movement, then releases one coherent navigation state.
      this.pageScrollFrame = window.requestAnimationFrame(() => {
        if (this.pageNavigation !== navigation) return;
        this.pageScrollFrame = window.requestAnimationFrame(() => {
          if (this.pageNavigation !== navigation) return;
          navigation.scrollingElement.scrollTop = navigation.target;
          this.finishPageNavigation(navigation);
        });
      });
    }

    finishPageNavigation(navigation) {
      if (this.pageNavigation !== navigation) return;

      this.pageScrollFrame = null;
      this.pageNavigation = null;
      this.releaseHeaderNavigationLease(navigation.headerLease);
      this.forcedActiveHeading = null;
      this.scheduleActiveHeadingUpdate();
    }

    releaseHeaderNavigationLease(lease) {
      if (!lease) return;

      const {
        isVisible,
        isLocked,
        listenerDetached,
        previousPreventHide,
        scrollHandler,
        sectionHeader,
        stickyHeader,
        toc,
        headerTopInlinePriority,
        headerTopInlineValue,
        tocTopInlinePriority,
        tocTopInlineValue,
      } = lease;
      const scrollingElement = document.scrollingElement;
      const pageScrollTop = scrollingElement?.scrollTop || window.scrollY;

      if (isLocked && pageScrollTop > 0) {
        if (!sectionHeader.classList.contains(HEADER_SCROLLED_CLASS)) {
          sectionHeader.classList.add(HEADER_SCROLLED_CLASS);
        }
        if (!sectionHeader.classList.contains(HEADER_STICKY_CLASS)) {
          sectionHeader.classList.add(HEADER_STICKY_CLASS);
        }
        const shouldBeHidden = !isVisible;
        if (
          sectionHeader.classList.contains(HEADER_HIDDEN_CLASS) !==
          shouldBeHidden
        ) {
          sectionHeader.classList.toggle(HEADER_HIDDEN_CLASS, shouldBeHidden);
        }
      } else if (isLocked) {
        sectionHeader.classList.remove(
          HEADER_SCROLLED_CLASS,
          HEADER_STICKY_CLASS,
          HEADER_HIDDEN_CLASS,
          HEADER_ANIMATE_CLASS,
        );
      }

      if (isLocked) {
        if (headerTopInlineValue) {
          sectionHeader.style.setProperty(
            "--ag-toc-navigation-header-top",
            headerTopInlineValue,
            headerTopInlinePriority,
          );
        } else {
          sectionHeader.style.removeProperty("--ag-toc-navigation-header-top");
        }
        if (tocTopInlineValue) {
          toc?.style.setProperty(
            "--ag-main-article-toc-top",
            tocTopInlineValue,
            tocTopInlinePriority,
          );
        } else {
          toc?.style.removeProperty("--ag-main-article-toc-top");
        }
        sectionHeader.removeAttribute(HEADER_NAVIGATION_LOCK_ATTRIBUTE);
      }

      if (stickyHeader) {
        stickyHeader.currentScrollTop = pageScrollTop;
        stickyHeader.preventHide = previousPreventHide;
      }
      if (
        listenerDetached &&
        typeof scrollHandler === "function" &&
        stickyHeader?.isConnected
      ) {
        window.addEventListener("scroll", scrollHandler, false);
      }
    }

    cancelPageNavigation() {
      if (this.pageScrollFrame !== null) {
        window.cancelAnimationFrame(this.pageScrollFrame);
      }
      this.pageScrollFrame = null;
      const navigation = this.pageNavigation;
      this.pageNavigation = null;
      this.releaseHeaderNavigationLease(navigation?.headerLease);
    }
  }

  class AgConsultationBadge extends HTMLElement {
    constructor() {
      super();
      this.button = null;
      this.closeButton = null;
      this.mobileQuery = null;
      this.isInitialized = false;
      this.handleClick = this.handleClick.bind(this);
      this.handleClose = this.handleClose.bind(this);
      this.handleConsultationClose = this.handleConsultationClose.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleBreakpointChange = this.handleBreakpointChange.bind(this);
      this.handlePointerLeave = this.handlePointerLeave.bind(this);
    }

    connectedCallback() {
      if (this.isInitialized) {
        return;
      }

      this.button = this.querySelector(CONSULTATION_BADGE_BUTTON_SELECTOR);
      this.closeButton = this.querySelector(CONSULTATION_BADGE_CLOSE_SELECTOR);

      if (!this.button || !this.closeButton) {
        return;
      }

      this.mobileQuery = window.matchMedia(CONSULTATION_BADGE_MOBILE_QUERY);
      this.isInitialized = true;
      this.button.addEventListener("click", this.handleClick);
      this.closeButton.addEventListener("click", this.handleClose);
      this.addEventListener("pointerleave", this.handlePointerLeave);
      window.addEventListener(
        CONSULTATION_CLOSE_EVENT_NAME,
        this.handleConsultationClose,
      );
      window.addEventListener("keydown", this.handleKeydown);

      if (this.mobileQuery.addEventListener) {
        this.mobileQuery.addEventListener(
          "change",
          this.handleBreakpointChange,
        );
      } else {
        this.mobileQuery.addListener(this.handleBreakpointChange);
      }

      this.syncDisclosureState();
    }

    disconnectedCallback() {
      if (this.button) {
        this.button.removeEventListener("click", this.handleClick);
      }

      if (this.closeButton) {
        this.closeButton.removeEventListener("click", this.handleClose);
      }

      this.removeEventListener("pointerleave", this.handlePointerLeave);
      window.removeEventListener(
        CONSULTATION_CLOSE_EVENT_NAME,
        this.handleConsultationClose,
      );
      window.removeEventListener("keydown", this.handleKeydown);

      if (this.mobileQuery?.removeEventListener) {
        this.mobileQuery.removeEventListener(
          "change",
          this.handleBreakpointChange,
        );
      } else {
        this.mobileQuery?.removeListener(this.handleBreakpointChange);
      }

      this.button = null;
      this.closeButton = null;
      this.mobileQuery = null;
      this.isInitialized = false;
    }

    isMobileViewport() {
      return (
        this.mobileQuery?.matches ??
        window.matchMedia(CONSULTATION_BADGE_MOBILE_QUERY).matches
      );
    }

    syncDisclosureState() {
      this.classList.remove("is-open", "is-hover-suppressed");
      this.closeButton.disabled = true;
      this.closeButton.setAttribute("aria-hidden", "true");
      this.closeButton.tabIndex = -1;

      if (this.isMobileViewport()) {
        this.button.setAttribute("aria-expanded", "false");
      } else {
        this.button.removeAttribute("aria-expanded");
      }
    }

    setExpanded(isExpanded, options = {}) {
      const { restoreFocus = false, suppressHover = false } = options;

      this.classList.toggle("is-open", isExpanded);
      this.button.setAttribute("aria-expanded", String(isExpanded));
      this.closeButton.disabled = !isExpanded;
      this.closeButton.setAttribute("aria-hidden", String(!isExpanded));
      this.closeButton.tabIndex = isExpanded ? 0 : -1;

      if (isExpanded) {
        this.classList.remove("is-hover-suppressed");
      } else if (suppressHover && this.matches(":hover")) {
        this.classList.add("is-hover-suppressed");
      }

      if (restoreFocus) {
        this.button.focus({ preventScroll: true });
      }
    }

    handleClose(event) {
      event.preventDefault();
      event.stopPropagation();
      this.setExpanded(false, {
        restoreFocus: true,
        suppressHover: true,
      });
    }

    handleConsultationClose() {
      if (
        !this.isMobileViewport() ||
        !this.classList.contains("is-open")
      ) {
        return;
      }

      this.setExpanded(false, { restoreFocus: true });
    }

    handleKeydown(event) {
      if (
        event.key !== "Escape" ||
        !this.isMobileViewport() ||
        !this.classList.contains("is-open")
      ) {
        return;
      }

      event.preventDefault();
      this.setExpanded(false, {
        restoreFocus: true,
        suppressHover: true,
      });
    }

    handleBreakpointChange() {
      this.syncDisclosureState();
    }

    handlePointerLeave() {
      this.classList.remove("is-hover-suppressed");
    }

    handleClick() {
      if (this.isMobileViewport() && !this.classList.contains("is-open")) {
        this.setExpanded(true);
        return;
      }

      const source = this.dataset.source || "page";
      const articleHandle = this.dataset.articleHandle || "";
      const productHandle = this.dataset.productHandle || "";
      const resourceHandle =
        this.dataset.resourceHandle || articleHandle || productHandle;
      const shouldOpenDefaultPopup = dispatchConsultationEvent(this.button, {
        source,
        articleHandle,
        productHandle,
        resourceHandle,
        ctaKey: FIXED_BADGE_CTA_KEY,
      });

      if (shouldOpenDefaultPopup) {
        openCalModal(this.dataset.calUrl, this.dataset.calAccent);
      }
    }
  }

  if (!customElements.get(CUSTOM_ELEMENT_NAME)) {
    customElements.define(CUSTOM_ELEMENT_NAME, AgMainArticle);
  }
  if (!customElements.get(CONSULTATION_BADGE_ELEMENT_NAME)) {
    customElements.define(CONSULTATION_BADGE_ELEMENT_NAME, AgConsultationBadge);
  }
  if (!customElements.get(CONSULTATION_MODAL_ELEMENT_NAME)) {
    customElements.define(CONSULTATION_MODAL_ELEMENT_NAME, AgConsultationModal);
  }
})();
